import React from 'react';
import get from 'lodash/get';

// Namespace
import * as ProposalNS from '../Proposal';
import * as ProposalElementsNS from '../ProposalElements';

// Utils
import { deepClone } from '../../../utils/objects';
import { formatDateWithLocale, getFinancialQuarterFromDate, getFinancialYearFromDate, formatLocalizedString } from '../../../utils/i18n';

import ProposalTextArea, { ProposalTextAreaProps } from '../components/views/ProposalTextArea';
import { DAOManager } from '../DAOs/DAOManager';
import { ProposalDAOInterface, ProposalDAOInterfaces, ProposalTextAreaDAOInterface } from '../DAOs/ProposalDAO';

// Types
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import { RiskProfileRule, RiskProfileRuleType, RiskProfileAssetAllocationRule } from '@/types/risk-profiles';

export const overrideItemColors = (selectedTemplate: ProposalNS.BaseProposalTemplate, items: ComparisonItem[]) => {
  const template = selectedTemplate as ProposalNS.ProposalTemplateV2;

  const sidebarElements = template.sidebarTabs.reduce((acc, tab) => acc.concat(tab.elements), [] as ProposalNS.ProposalSidebarElement[]);
  const itemSelector = sidebarElements.find(
    tab => tab.type === ProposalNS.ProposalItemSelectionType.ITEM_SELECTOR || tab.type === ProposalNS.ProposalItemSelectionType.ITEM_LISTER,
  );

  const newItems = deepClone(items);

  if (itemSelector) {
    // Vague TODO - move name overrides here too.

    const { customParameters } = template;
    const { itemColorOverride } = itemSelector as ProposalNS.ProposalItemSelection;

    if (itemColorOverride) {
      if (items.length === 1) {
        if (customParameters && customParameters.singleItemShift) {
          newItems[0].color = itemColorOverride[1];
        } else {
          newItems[0].color = itemColorOverride[0];
        }
      } else {
        for (let i: number = 0; i < itemColorOverride.length; i++) {
          if (items[i]) {
            newItems[i].color = itemColorOverride[i];
          }
        }
      }
    }
  }

  return newItems;
};

export const processWildcardString = (inStr: string, proposal: ProposalNS.Proposal, locale: Locale, metadata?: ProposalNS.DAOGetTextsMetadata): string => {
  let workStr: string = inStr;

  if (metadata && metadata.sectionIteratorIndex !== undefined) {
    workStr = workStr.replace('{{sectionIteratorIndex}}', metadata.sectionIteratorIndex.toString());
  }

  // eslint-disable-next-line no-useless-escape
  const re = /\{\{[\w\.,_\|\/]+\}\}/g;
  const resultArray = workStr.match(re);

  if (resultArray && resultArray.length > 0) {
    const result = resultArray[0];
    const params = result.replace('{{', '').replace('}}', '').split('|');
    const rawExpression: string = params[0];
    let type: string | undefined;
    let config: string | undefined;
    if (params.length > 2) {
      type = params[1].trim();
      config = params[2].trim();
    }

    const expressions: string[] = rawExpression.split('.');
    const needsLocale: boolean = expressions[expressions.length - 1] === 'locale';

    // take 'locale' out as it is not part of the property path
    if (needsLocale) {
      expressions.pop();
    }

    let base: object | undefined;

    if (expressions[0] === 'proposal') {
      base = proposal;
    } else if (expressions[0] === 'metadata' && metadata !== undefined) {
      base = metadata;
    } else if (expressions[0] === 'item' && metadata && metadata.itemIndex !== undefined) {
      base = proposal.items[metadata.itemIndex]; // Can still be undefined
    } else if (expressions[0] === 'itemIndex') {
      expressions.shift();
      const index: number = Number.parseInt(expressions[0]);
      base = proposal.items[index]; // Can still be undefined
    }

    if (base !== undefined) {
      expressions.shift(); // Take the base out as it is not part of the property path.

      let textToDisplay: string = '';

      if (expressions[0] === 'riskProfile' && ['equity', 'fixedIncome', 'cash'].includes(expressions[1])) {
        // This is a temporary solution to avoid reworking the base CapIntel templates to
        // use the new rule-based solution [CON-7267] because of time constraints.
        // The long-term solution is to refactor the base templates to use the new risk profiles
        // solution, and get rid of this piece here.

        expressions.shift();
        const targets = parseRiskProfileTargets(base, expressions);

        if (type === 'UNDEFINED') {
          textToDisplay = targets ? '' : config?.replace(/_/g, '-') || '';
        } else {
          // A bit of a hack, but if the specified risk profile target has the same min/max values,
          // the targets result will be empty for the min value - in which case we'll completely hide
          // the element. Not a maintainable solution, but this should all be gone once we move all
          // risk profiles to the new handler.
          if (!targets) {
            return '';
          }

          textToDisplay = targets;
        }
      } else if (type !== undefined && type === 'DATE') {
        textToDisplay = getDate(base, expressions, locale, config !== undefined ? config : '');
      } else if (type !== undefined && type === 'UNDEFINED') {
        textToDisplay = getUndefined(base, expressions, config !== undefined ? config : '');
      } else if (type !== undefined && type === 'FALSE') {
        textToDisplay = getFalse(base, expressions, config !== undefined ? config : '');
      } else if (type !== undefined && type === 'FQUARTER') {
        textToDisplay = getFinancialQuarter(base, expressions, config !== undefined ? config : '');
      } else if (type !== undefined && type === 'FYEAR') {
        textToDisplay = getFinancialYear(base, expressions, config !== undefined ? config : '');
      } else {
        textToDisplay = getString(base, expressions, locale, needsLocale);
      }

      if (metadata && metadata.textRegex && metadata.textRegex.type === ProposalNS.TextRegexType.REPLACE) {
        textToDisplay = textToDisplay.replace(new RegExp(metadata.textRegex.regexp, 'g'), metadata.textRegex.newSubStr);
      }

      workStr = workStr.replace(result, textToDisplay);
    }
  }

  return workStr;
};

export const filterSectionsByType = (sections: Array<ProposalNS.ProposalTemplateSectionElement>, type: ProposalNS.ProposalSectionType) =>
  sections.reduce((sectionsKeys: string[], section: ProposalNS.ProposalTemplateSectionElement) => {
    const { key, sectionType } = section;

    if (!key || !sectionType) {
      return sectionsKeys;
    }

    if (sectionType.includes(type)) {
      sectionsKeys = [...sectionsKeys, key];
    }

    return sectionsKeys;
  }, []);

/**
 * This function sorts and filters an array of sectionKey(s) based on a specific type and returns corresponding sections from another array.
 * @param sections - Array of strings representing different sections from the proposalTemplateOptions.
 * @param rootSections - Array of objects representing proposal template sections
 * @returns Sorted array of ProposalTemplateSectionElement objects in order provided by the sorted sectionKey(s)
 */
export const sortSectionsBySectionKey = (
  sections: Array<string>,
  rootSections: Array<ProposalNS.ProposalTemplateSectionElement>,
  sectionType: ProposalNS.ProposalSectionType,
) => {
  const sectionKeys = sections.filter(section => section.includes(sectionType));
  const sortedSectionKeys = sectionKeys.sort((a, b) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  });

  return sortedSectionKeys.reduce((acc: Array<ProposalNS.ProposalTemplateSectionElement>, currentSectionKey: string) => {
    const selectedSection = rootSections.find(section => section.key && section.key.includes(currentSectionKey));
    if (selectedSection) {
      return acc.concat(selectedSection);
    }
    return acc;
  }, []);
};

const parseRiskProfileTargets = (base: object, expressions: string[]): string | null => {
  const rules = get(base, 'riskProfile.rules', []) as RiskProfileRule[];
  const rule = rules.find(targetRule => targetRule.ruleType === RiskProfileRuleType.AssetAllocation) as RiskProfileAssetAllocationRule;

  if (!rule) {
    return '';
  }

  let target;
  switch (expressions[0]) {
    case 'equity': {
      target = rule.value.find(val => val.assetType === 'EQUITY');
      break;
    }
    case 'fixedIncome': {
      target = rule.value.find(val => val.assetType === 'FIXED_INCOME');
      break;
    }
    case 'cash': {
      target = rule.value.find(val => val.assetType === 'CASH');
      break;
    }
  }

  if (!target) {
    return '';
  }

  if (expressions[1] === 'min') {
    if (target.min === target.max) {
      return null;
    }

    return target.min.toString();
  }
  return target.max.toString();
};

const getUndefined = (base: object, expressions: string[], config: string): string => {
  const result: unknown = get(base, expressions, undefined);

  if (result === undefined || result === '') {
    return config.replace(/_/g, '-');
  }

  return '';
};

const getFalse = (base: object, expressions: string[], config: string): string => {
  const result: unknown = get(base, expressions, undefined);

  if (!result) {
    return config.replace(/_/g, '-');
  }

  return '';
};

const getDate = (base: object, expressions: string[], locale: Locale, config: string): string => {
  let result: unknown = get(base, expressions, undefined);

  // filter out undefined and null
  if (result == null) {
    return '';
  }

  if (typeof result === 'string') {
    result = new Date(result);
  }

  if (result instanceof Date) {
    return formatDateWithLocale(result, locale, config.replace(/_/g, ' '));
  }

  return '';
};

const getFinancialQuarter = (base: object, expressions: string[], config: string): string => {
  let result: unknown = get(base, expressions, undefined);

  // filter out undefined and null
  if (result == null) {
    return '';
  }

  if (typeof result === 'string') {
    result = new Date(result);
  }

  if (result instanceof Date) {
    return getFinancialQuarterFromDate(result, config).toString();
  }

  return '';
};

const getFinancialYear = (base: object, expressions: string[], config: string): string => {
  let result: unknown = get(base, expressions, undefined);

  // filter out undefined and null
  if (result == null) {
    return '';
  }

  if (typeof result === 'string') {
    result = new Date(result);
  }

  if (result instanceof Date) {
    return getFinancialYearFromDate(result, config).toString();
  }

  return '';
};

const getString = (base: object, expressions: string[], locale: Locale, needsLocale: boolean): string => {
  let result: unknown = get(base, expressions, undefined);

  // filter out undefined and null
  if (result == null) {
    return '';
  }

  // first if we know we need a LocalizedString - test it first
  if (needsLocale) {
    if (typeof result !== 'object') {
      return '';
    }

    if (result == null) {
      return '';
    }

    // we get the string and forward it to the rest of the logic.
    result = formatLocalizedString(result, locale);
  }

  // is it a string?
  if (typeof result === 'string') {
    return result;
  }

  // is it a number?
  if (typeof result === 'number') {
    return result.toString();
  }

  // value is invalid
  return '';
};

export const getTextarea = (proposal: ProposalNS.Proposal, setTextAreaData: (key: string, textAreaData: ProposalTextAreaData) => void, config: any): any => {
  const textAreaOptionConfig = {
    key: config.key,
    maxChars: config.maxChars,
    placeholder: config.placeholder,
    default: config.default,
  };

  const data: ProposalElementsNS.ProposalDataElement = {
    type: ProposalNS.ProposalTemplateElementType.Data,
    dataView: {
      type: ProposalNS.DataView.TextArea,
      className: config.className,
    },
    DAO: {
      type: ProposalNS.DAOType.Commentary,
      commentaryDAOOptions: {
        key: config.key,
      },
    },
    textAreaOptionConfig,
    pageOverflowIndex: 0,
    elementType: ProposalElementsNS.ProposalElementType.Data,
  };

  const dao: ProposalDAOInterface = DAOManager.getInstance().buildDAO(data.DAO.type as ProposalNS.DAOType, proposal, data);
  const localDAO: ProposalTextAreaDAOInterface | undefined = dao.getInterface<ProposalTextAreaDAOInterface>(ProposalDAOInterfaces.TextArea);
  if (localDAO === undefined) {
    return '';
  }

  const element = React.createElement<ProposalTextAreaProps>(ProposalTextArea, {
    proposal,
    textAreaOptionConfig,
    className: config.className,
    initialValue: localDAO.getTextAreaInitialValue(textAreaOptionConfig && textAreaOptionConfig.default),
    setTextAreaData,
  });

  return element;
};
